import { formatNumber } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID } from '@angular/core';

import { CadsLabeledAmountBitConfig } from './cads-labeled-amount-bit.config';

/**
 * Label + currency amount bit: small-caps label and a bold amount whose **$** is always rendered by the component (not passed
 * in by the host). Figures use {@link formatNumber} with the app locale (two decimals)—equivalent to an en-US currency string
 * without swapping the fixed `$` for another symbol. Options via {@link CadsLabeledAmountBitConfig}.
 */
@Component({
  selector: 'cads-labeled-amount-bit',
  standalone: true,
  templateUrl: './cads-labeled-amount-bit.component.html',
  styleUrl: './cads-labeled-amount-bit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CadsLabeledAmountBitComponent {
  private readonly _locale = inject(LOCALE_ID);

  readonly config = input.required<CadsLabeledAmountBitConfig>();

  readonly labelClass = computed(() => {
    const size = this.config().size ?? 'medium';
    return `cads-labeled-amount-bit__label cads-labeled-amount-bit__label--${size}`;
  });

  readonly amountClass = computed(() => {
    const size = this.config().size ?? 'medium';
    return `cads-labeled-amount-bit__amount cads-labeled-amount-bit__amount--${size}`;
  });

  /** Formatted figures only (no currency symbol); `$` is output in the template. */
  readonly amountFigures = computed(() => {
    const amount = this.config().amount;
    const digits =
      this.config().compact && Math.round(amount * 100) % 100 === 0 ? '1.0-0' : '1.2-2';
    return formatNumber(amount, this._locale, digits);
  });
}
